import { FC, ReactNode, useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, useOutletContext } from 'react-router-dom';

import { useTranslation, useLanguages } from '@app/app/i18n';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuPortal,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from '@app/shared/ui/dropdown-menu';
import { cn } from '@app/app/lib';
import { replaceLanguagePrefix } from '@app/app/i18n/utils/language-url';
import { setLanguageCookie } from '@app/app/i18n/utils/language-cookie';
import { useAppSelector } from '@app/hooks/use-store';
import { useUpdateOptionsMutation } from '@app/store/api/user.api';
import type { CommonOutletContext } from '@app/app/layouts';

type LanguageSwitcherProps = {
  trigger: ReactNode;
  onClose?: () => void;
  itemTextSize?: 'text-[14px]' | 'text-[16px]';
  side?: 'left' | 'right' | 'top' | 'bottom';
};

export const LanguageSwitcher: FC<LanguageSwitcherProps> = ({
  trigger,
  onClose,
  itemTextSize = 'text-[14px]',
  side = 'left',
}) => {
  const { currentLanguage, changeLanguage } = useTranslation('header');
  const languages = useLanguages('header');
  const [isOpen, setIsOpen] = useState(false);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAppSelector((state) => state.authState.isAuthenticated);
  const { user_ctx } = useOutletContext<CommonOutletContext>();
  const [updateOptions] = useUpdateOptionsMutation();

  // Альтернативная проверка авторизации через user_ctx (более надежная)
  const isUserAuthenticated = isAuthenticated || !!user_ctx?.id;

  const handleLanguageChange = async (langCode: string) => {
    console.log('[LanguageSwitcher] handleLanguageChange called', {
      langCode,
      isAuthenticated,
      isUserAuthenticated,
      user_ctx: user_ctx ? { id: user_ctx.id } : null,
      currentPathname: location.pathname,
    });

    // 1. Обновляем куку (сначала меняем куку, потом вызываем API)
    // Пользователь явно меняет язык - устанавливаем куку с принудительным обновлением
    setLanguageCookie(langCode, true);
    console.log('[LanguageSwitcher] Cookie updated');

    // 2. Обновляем язык в i18n
    changeLanguage(langCode);
    console.log('[LanguageSwitcher] i18n language changed');

    // 3. Обновляем URL с новым языковым префиксом
    const newPath = replaceLanguagePrefix(location.pathname, langCode);
    if (newPath !== location.pathname) {
      // Сохраняем query параметры при редиректе
      const newUrl = `${newPath}${location.search}`;
      console.log('[LanguageSwitcher] Navigating to new path', {
        from: location.pathname,
        to: newUrl,
      });
      navigate(newUrl, { replace: true });
    }

    // 4. Если авторизован - сохраняем язык в профиль через POST /update-options/
    console.log('[LanguageSwitcher] Checking authentication', {
      isAuthenticated,
      isUserAuthenticated,
      user_ctx: user_ctx ? { id: user_ctx.id } : null,
    });
    if (isUserAuthenticated) {
      try {
        console.log('[LanguageSwitcher] Calling POST /update-options/ with language', { langCode });
        const formData = new FormData();
        formData.append('language', langCode);
        const result = await updateOptions({ body: formData }).unwrap();
        console.log('[LanguageSwitcher] Language saved to profile successfully', { result });
      } catch (error) {
        console.error('[LanguageSwitcher] Failed to save language to profile:', error);
        // Не блокируем переключение языка, если сохранение в профиль не удалось
      }
    } else {
      console.log('[LanguageSwitcher] User is not authenticated, skipping API call');
    }

    setIsOpen(false);
    onClose?.();
  };

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 200);
  };

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen} modal={false}>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        <DropdownMenuPortal>
          <DropdownMenuContent
            align="end"
            side={side}
            sideOffset={4}
            className="p-1 min-w-[140px] z-[200]"
          >
            {languages.map((lang) => (
              <DropdownMenuItem
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={cn(
                  'cursor-pointer py-2 px-3 flex items-center gap-2',
                  itemTextSize,
                  currentLanguage === lang.code
                    ? 'text-general_MI_500_default'
                    : 'text-general_text_MII_secondary_i',
                )}
              >
                <span>{lang.label}</span>
                <span>{lang.flag}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenuPortal>
      </DropdownMenu>
    </div>
  );
};
